import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Play,
  History,
  Gift,
  CheckCircle
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import JobCard from '../components/JobCard';
import ProviderSelection from '../components/ProviderSelection';
import axios from 'axios';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const { user, getFlickrAccount } = useAuthStore();
  const flickrAccount = getFlickrAccount();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [freeTrialStatus, setFreeTrialStatus] = useState(null);

  useEffect(() => {
    // Only fetch stats when user is loaded and fully authenticated
    // Avoid refetching if stats already exist and user hasn't changed
    if (user && !user.isTemporary && !stats) {
      fetchStats();
    } else if (user?.isTemporary) {
      // User is email verified but no provider connected yet
      setLoading(false);
    } else if (user && !user.isTemporary && stats) {
      // User is loaded and stats already exist - just stop loading
      setLoading(false);
    }
  }, [user, stats]);

  const fetchStats = async () => {
    try {
      const [statsResponse, freeTrialResponse] = await Promise.all([
        axios.get('/api/jobs/stats/summary', { withCredentials: true }),
        axios.get('/api/users/free-trial-status', { withCredentials: true })
      ]);
      
      setStats(statsResponse.data);
      setFreeTrialStatus(freeTrialResponse.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      
      // Provide more specific error messages
      if (error.response?.status === 429) {
        toast.error('Too many requests. Please wait a moment before refreshing.');
      } else {
        toast.error('Failed to load dashboard data');
      }
      
      // Set empty stats to prevent repeated attempts
      setStats({});
      setFreeTrialStatus({ isEligible: false, remainingPhotos: 0 });
    } finally {
      setLoading(false);
    }
  };



  const handleJobClick = (jobId) => {
    navigate(`/job/${jobId}`);
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow-sm">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome back, {flickrAccount?.displayName || user?.email?.split('@')[0] || 'there'}!
        </h1>
        <p className="text-xl text-gray-600">
          Ready to make your photos shine?
        </p>
      </div>

      {user?.isTemporary && (
        <div className="mb-6">
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="mb-4 sm:mb-0 sm:mr-4">
              <h2 className="text-lg font-semibold text-blue-900">Finish securing your account</h2>
              <p className="text-sm text-blue-800 mt-1">
                Create a password now so you can sign in with email and password any time.
              </p>
            </div>
            <Link
              to="/settings/password"
              className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Create a password
            </Link>
          </div>
        </div>
      )}

      {/* Progress Steps */}
      <div className="flex justify-center mb-10">
        <div className="flex items-center space-x-4 bg-white px-6 py-3 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-medium">1</div>
            <span className="ml-2 text-sm font-medium text-primary-600">Select Photos</span>
          </div>
          <div className="w-8 h-px bg-gray-300"></div>
          <div className="flex items-center">
            <div className="w-6 h-6 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center text-xs font-medium">2</div>
            <span className="ml-2 text-sm text-gray-500">Review Metadata</span>
          </div>
          <div className="w-8 h-px bg-gray-300"></div>
          <div className="flex items-center">
            <div className="w-6 h-6 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center text-xs font-medium">3</div>
            <span className="ml-2 text-sm text-gray-500">Apply to Photos</span>
          </div>
        </div>
      </div>

      {/* Connect Account Section - Show when no accounts connected */}
      {(!user?.connectedAccounts || user.connectedAccounts.length === 0) && (
        <div className="mb-12">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Connect Your First Photo Platform
            </h2>
            <p className="text-gray-600">
              Choose a platform to connect and start enhancing your photos with AI
            </p>
          </div>
          <ProviderSelection 
            user={user}
            title=""
            subtitle=""
            showExisting={false}
            compact={true}
            className="flex justify-center"
          />
        </div>
      )}

      {/* Hero CTA Section - Show when accounts are connected */}
      {user?.connectedAccounts && user.connectedAccounts.length > 0 && (
        <div className="text-center mb-12">
          <div className="max-w-md mx-auto">
            {/* Visual Illustration */}
            <div className="mb-6">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-primary-100 to-secondary-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>

          {/* Provider Selection Buttons */}
          {user.connectedAccounts.length === 1 ? (
            // Single provider - direct link
            <Link
              to="/photos"
              state={{ provider: user.connectedAccounts[0].provider }}
              className="inline-block bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
            >
              {freeTrialStatus && freeTrialStatus.isEligible && freeTrialStatus.remainingPhotos > 0 ? (
                <>
                  Enhance My {user.connectedAccounts[0].provider.charAt(0).toUpperCase() + user.connectedAccounts[0].provider.slice(1)} Photos ({freeTrialStatus.remainingPhotos} Free)
                  <Gift className="inline w-5 h-5 ml-2" />
                </>
              ) : (
                <>
                  Enhance My {user.connectedAccounts[0].provider.charAt(0).toUpperCase() + user.connectedAccounts[0].provider.slice(1)} Photos
                  <Play className="inline w-5 h-5 ml-2" />
                </>
              )}
            </Link>
          ) : (
            // Multiple providers - show selection
            <div className="space-y-4">
              <p className="text-gray-600 mb-4">Select which photo library to enhance:</p>
              <div className="flex flex-col space-y-3">
                {user.connectedAccounts.map(account => (
                  <Link
                    key={account.provider}
                    to="/photos"
                    state={{ provider: account.provider }}
                    className="inline-flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                  >
                    <span className="mr-2">
                      {account.provider === 'flickr' && '📷'}
                      {account.provider === 'unsplash' && '🌄'}
                      {account.provider === 'smugmug' && '📸'}
                    </span>
                    Enhance My {account.provider.charAt(0).toUpperCase() + account.provider.slice(1)} Photos
                    {freeTrialStatus && freeTrialStatus.isEligible && freeTrialStatus.remainingPhotos > 0 && (
                      <Gift className="inline w-4 h-4 ml-2" />
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )}
          
          <p className="text-gray-600 mt-4 text-base leading-relaxed">
            Select your photos and let AI create titles, captions, and tags automatically.
          </p>

          {/* Free Trial Details */}
          {freeTrialStatus && freeTrialStatus.isEligible && freeTrialStatus.remainingPhotos > 0 && (
            <div className="mt-4 inline-flex items-center px-4 py-2 bg-green-50 text-green-700 rounded-lg border border-green-200">
              <CheckCircle className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">
                {freeTrialStatus.hasUsedFreeTrial ? 
                  `${freeTrialStatus.remainingPhotos} free photos remaining` : 
                  `${freeTrialStatus.maxPhotos || 10} free photos to try`
                }
              </span>
            </div>
          )}
          </div>
        </div>
      )}

      {/* Secondary Actions - Only show when accounts are connected */}
      {user?.connectedAccounts && user.connectedAccounts.length > 0 && (
        <div className="flex justify-center mb-8">
          <div className="flex space-x-6 text-sm">
            <Link
              to="/history"
              className="flex items-center text-gray-500 hover:text-gray-700 transition-colors"
            >
              <History className="w-4 h-4 mr-1" />
              View History
            </Link>
            <Link
              to="/settings"
              className="flex items-center text-gray-500 hover:text-gray-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Preferences
            </Link>
          </div>
        </div>
      )}

      {/* Connect More Platforms Section - Show when user has accounts but could connect more */}
      {user?.connectedAccounts && user.connectedAccounts.length > 0 && user.connectedAccounts.length < 3 && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-6 mb-8">
          <div className="text-center mb-6">
            <div className="w-12 h-12 mx-auto bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Connect More Photo Platforms
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed max-w-2xl mx-auto">
              Maximize your photo management by connecting all your platforms. Access and enhance photos from multiple sources in one streamlined workflow.
            </p>
          </div>
          
          <ProviderSelection 
            user={user}
            title=""
            subtitle=""
            showExisting={false}
            compact={true}
          />
          
          <div className="mt-4 text-center">
            <Link
              to="/settings"
              className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
            >
              Manage all connections in Settings →
            </Link>
          </div>
        </div>
      )}
    
      {/* Recent Activity */}
      {stats?.recentJobs && stats.recentJobs.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {stats.recentJobs.map((job, index) => (
              <JobCard
                key={job._id || index}
                job={job}
                variant="compact"
                onClick={handleJobClick}
              />
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default Dashboard;

